// HeroHost — the empty-conversation HERO surface's impure-shell host, extracted
// from AppShellHost (the composition root) so the landing-hero feature owns its
// own file instead of colliding on the shared hot file (split-the-hot-file,
// .claude/analysis/dev-orchestration-model.md §10a). Mirrors useEditorHost /
// useNavHost: the shell computes every dependency and threads it in through ONE
// named `HeroHostDeps`; this hook hands back the ready `heroLayoutFor` render
// function the shell's `conversationCentre` discriminant calls for an empty
// conversation. Pure-shell discipline holds — the host owns no state and adds no
// effects; every value and handler arrives via the explicit deps object (deps
// arrive via context/props), and the hero component tree stays pure. This is a
// behaviour-preserving MOVE of the former in-shell `heroLayoutFor` closure.

import { useLingui } from "@lingui/react/macro";
import {
    type Dispatch,
    type ReactNode,
    type SetStateAction,
    useEffect,
    useState,
} from "react";
import type { ArcProminence } from "../../app/coaching.js";
import { composerAttachmentsFor } from "../../app/composerAttachments.js";
import { addedMentions, removedMentions } from "../../app/contextMentions.js";
import type { FrameTransition } from "../../app/frame.js";
import type { IngestFile, IngestId } from "../../app/ingest.js";
import {
    type PendingGrounding,
    pendingGroundingFor,
} from "../../app/pendingGrounding.js";
import type { Library } from "../../app/ports.js";
import { situationSummary } from "../../app/summaryProjection.js";
import type { ChatSession } from "../../modules/chat/model.js";
import {
    type ChatCommand,
    nascentComposer,
    type TurnFailure,
} from "../../modules/chat.js";
import type { SelectableContext } from "../Chat/types.js";
import type {
    ComposerResourceRef,
    ComposerSlashCommand,
} from "../Composer/PowerComposer/types.js";
import type {
    RailCategory,
    ResourceId,
    ResourceRef,
} from "../Navigation/types.js";
import type { PipelineProgress } from "../WorkspaceLanding/pipelineArc.js";
import type {
    CapabilityTip,
    LandingState,
    RecentThread,
} from "../WorkspaceLanding/WorkspaceLanding.js";
import { WorkspaceLanding } from "../WorkspaceLanding/WorkspaceLanding.js";

// The full dependency surface the hero render pulls from the shell. Every field
// is a value the shell already owns (state, derived reads) or a handler it
// already declares — the host reaches for nothing global. `isFreshForAuthor`
// is the coaching model's per-author signal, computed by CoachHost and threaded
// through the shell so the two surfaces stay in step. `toComposerResourceRef`
// is passed rather than imported to keep the shell the single owner of that
// module helper (composerCandidates also uses it) and avoid a shell import cycle.
export type HeroHostDeps = {
    readonly isFreshForAuthor: boolean;
    readonly conversationSummaries: readonly RecentThread[];
    // The wall clock the summary's recency label ("senast sammanfattad")
    // formats against — read by the shell through the sanctioned `useNow`
    // seam (hooks/useNow.ts) and threaded in as a plain number, so this host
    // and its render tree stay Date-free (docs/implement/ui-as-projection.md).
    readonly nowMs: number;
    // How loudly the outcome arc presents (Dm4), derived upstream from the
    // author's experience ladder (CoachHost). Never a raw toggle. INTEGRATOR
    // PROP-REQUEST: relay `coach.arcProminence`. Absent ⇒ `prominent`.
    readonly arcProminence?: ArcProminence;
    // Start the guided tour (D28), relayed from CoachHost.
    readonly requestGuidedTour: () => void;
    readonly pendingGrounding: PendingGrounding;
    // The composer's references (the `@`-picks, and the Studio's selected
    // cards): shown and removable here as in the chat's own composer.
    readonly composerMentions: readonly ComposerResourceRef[];
    // F83: the transient sources the composer's optimistic attachment chips
    // fold from. `ingestBatch` is the shell's mirror of the ingest decider's
    // per-file progress; `ingestConversations` maps each queued file to the
    // conversation active at drop time. Both VIEW-TRANSIENT, reconciled by the
    // durable file_landed event — never a durable field.
    readonly ingestBatch: readonly IngestFile[];
    readonly ingestConversations: ReadonlyMap<IngestId, string>;
    readonly selectableContexts: readonly SelectableContext[];
    readonly composerCandidates: readonly ComposerResourceRef[];
    readonly chatSlashCommands: readonly ComposerSlashCommand[];
    readonly displayName: string;
    // The semantic-summary trust surface (IDD 94442b9d): the loaded
    // library carries BOTH the real consent and the latest generated
    // prose server-side (`library.summary` — `app/summaryProjection.ts`);
    // `null` while not yet loaded ⇒ the summary slot degrades to `null`,
    // matching the library's own loading gap elsewhere. The three
    // consent-flow handlers are wired straight to the `setSummaryConsent`
    // port + the manual refresh trigger.
    readonly library: Library | null;
    readonly historicalPlayback: boolean;
    readonly tipsDismissed: boolean;
    readonly setTipsDismissed: Dispatch<SetStateAction<boolean>>;
    readonly tipIndex: number;
    readonly sessionFor: (conversationId: string) => ChatSession;
    readonly refFor: (id: ResourceId) => ResourceRef | undefined;
    readonly toComposerResourceRef: (ref: ResourceRef) => ComposerResourceRef;
    readonly openFilePicker: () => void;
    readonly sendChat: (command: ChatCommand) => void;
    readonly submitText: (raw: string, conversationIdOverride?: string) => void;
    readonly retryTurn: (conversationId: string) => void;
    readonly stopTurn: (conversationIdOverride?: string) => void;
    readonly turnFailureText: (failure: TurnFailure) => string;
    readonly openConversation: (conversationId: string) => void;
    readonly selectCategory: (category: RailCategory) => void;
    readonly dispatch: Dispatch<FrameTransition>;
};

// The first-message TRANSITION (owner: "liten, snabb" — small, fast): once
// the shell has already flipped a conversation to non-empty, it can still
// render the hero for ONE more beat in the "exiting" phase so the composer's
// glide + the caption/CTA fade (WorkspaceLanding.css) has time to play before
// the real swap to the transcript. `onExited` is WorkspaceLanding's own
// `transitionend` reporting up — never a bare timer standing in for it (see
// `HeroToChatTransition` below, which owns the hold + a bounded safety-net
// fallback). Absent `transition` ⇒ the ordinary resting hero (unchanged).
export type HeroTransitionProps = {
    readonly phase: "exiting";
    readonly onExited: () => void;
};

export type HeroHostApi = {
    // The empty-conversation centre for a given conversation tab. The shell's
    // `conversationCentre` calls this when the conversation's transcript is
    // empty; a non-empty transcript stays the shell's `chatFullFor`. The
    // optional second argument is the first-message exit hold above — every
    // other call site simply omits it and gets the resting hero.
    readonly heroLayoutFor: (
        conversationId: string,
        transition?: HeroTransitionProps,
    ) => ReactNode;
};

export const useHeroHost = (deps: HeroHostDeps): HeroHostApi => {
    const { t, i18n } = useLingui();
    const {
        isFreshForAuthor,
        conversationSummaries,
        nowMs,
        arcProminence = { type: "prominent" },
        requestGuidedTour,
        pendingGrounding,
        composerMentions,
        ingestBatch,
        ingestConversations,
        composerCandidates,
        chatSlashCommands,
        displayName,
        library,
        historicalPlayback,
        tipsDismissed,
        setTipsDismissed,
        tipIndex,
        sessionFor,
        refFor,
        toComposerResourceRef,
        openFilePicker,
        sendChat,
        submitText,
        retryTurn,
        stopTurn,
        turnFailureText,
        openConversation,
        selectCategory,
        dispatch,
    } = deps;

    const heroLayoutFor = (
        conversationId: string,
        transition?: HeroTransitionProps,
    ): ReactNode => {
        const session = sessionFor(conversationId);
        // The AI-first landing (home-composer):
        //   • summary — the semantic-summary consent-flow model (IDD
        //     94442b9d), `null` only while the library hasn't loaded yet.
        //     Both consent AND the summary's own prose are real,
        //     server-projected data now (`library.summary` —
        //     `app/summaryProjection.ts`) — the detail view shows the
        //     honest "not generated yet" state only when that is actually
        //     true, never as a permanent stand-in for an unwired read.
        //   • threads — `listConversations` supplies real aggregate ids/titles;
        //     an empty result remains an honest empty state.
        // Tips are real, static app capabilities. (The former resources aside
        // was cut — resources stay reachable via the composer's "+" attach and
        // the omnibar.)
        const capabilityTips: readonly CapabilityTip[] = [
            {
                kind: "try",
                text: t`typing a topic to draft a quiz or a lesson`,
            },
            {
                kind: "drop",
                text: t`a PDF onto the composer to ground the AI in your own material`,
            },
            {
                kind: "ask",
                text: t`the assistant to package a material for Torus, Canvas or Markdown`,
            },
            { kind: "try", text: t`⌘K to jump to any resource or command` },
        ];
        // Fresh (this author's picture) → the `new` degradation (composer alone,
        // no summary / recents); otherwise `returning` with the recent
        // conversations. Same perspective-scoped projection the guide keys on, so
        // the two stay in step.
        const landingState: LandingState = isFreshForAuthor
            ? { type: "new" }
            : {
                  type: "returning",
                  summary:
                      library === null
                          ? null
                          : situationSummary(
                                library.summary,
                                nowMs,
                                i18n.locale,
                            ),
                  threads: conversationSummaries,
              };
        // The composer's grounded mentions ARE this nascent conversation's
        // pending grounding (§3.3) — picking/removing mutates the local map, no
        // durable write until the first send flushes it. Converted straight from
        // the held refs (no library round-trip needed to display them).
        const pendingItems: readonly ComposerResourceRef[] =
            pendingGroundingFor(pendingGrounding, conversationId).map(
                toComposerResourceRef,
            );
        // F83: the DISPLAYED chip list folds the optimistic ingest signals in
        // on top of the grounded set, each carrying its lifecycle status
        // (uploading → ready, or failed). A dropped file
        // shows here the instant `files_queued` folds — no server round-trip.
        // Reconciliation below still keys off `pendingItems` (the grounded
        // baseline): the optimistic chips carry synthetic `ingest:<id>` ids that
        // resolve to nothing durable, so removing/keeping them is a no-op there —
        // they self-heal when the real source lands.
        const attachmentChips: readonly ComposerResourceRef[] =
            composerAttachmentsFor(
                conversationId,
                ingestBatch,
                ingestConversations,
                pendingGrounding,
            );
        // The references first, then the attachments not already among them
        // (the same fold the chat's own composer shows — modules/chat/view).
        const composerChips: readonly ComposerResourceRef[] = [
            ...composerMentions,
            ...attachmentChips.filter(
                (c) => !composerMentions.some((m) => m.id === c.id),
            ),
        ];
        // The CURRENT-TASK authoring progress (F-D / DB2) — task-scoped signals
        // only, never the workspace library:
        //   • hasSource — this conversation has a grounded source (auto-grounded
        //     on add, DB4), so the arc advances the moment a source is attached.
        //   • hasAsk    — the author has asked (an author turn) or a material is
        //     being built / a turn is in flight.
        //   • hasMaterial — the transcript carries a settled MaterialEntry.
        //   • hasPackage — no task-scoped signal yet (Dm3), so it rests upcoming.
        // On the hero the transcript is empty by construction (a non-empty one
        // flips to the chat surface), so `ask`/`material` sit false there — the
        // arc honestly shows only what THIS nascent task has done.
        const taskProgress: PipelineProgress = {
            hasSource: attachmentChips.length > 0,
            hasAsk:
                session.drafting ||
                session.conversation.some(
                    (e) =>
                        e.type === "author" || e.type === "material_building",
                ),
            hasMaterial: session.conversation.some(
                (e) => e.type === "material",
            ),
            hasPackage: false,
        };
        // The situation-profile pick ("who this is for") and the "what grounds
        // this" ContextSideCard used to ride here as a RIGHT-hand aside — that
        // box is REMOVED from the current UI (owner request): the composer's
        // own inline `@`-mention pills are now the single display of attached
        // sources, and standing up "who this is for" again on the hero is
        // deferred to the future chat-shell Context pane. The underlying
        // pendingGrounding/situation-profile state machinery (useContextSpine,
        // pendingGrounding.ts) is UNCHANGED — only this card's mount is gone.
        return (
            <WorkspaceLanding
                state={landingState}
                displayName={displayName}
                // Chat's own wiring of the nascent composer (draft, first
                // send, stop, retry, send state): modules/chat/view.tsx.
                {...nascentComposer(
                    conversationId,
                    session,
                    { historicalPlayback, failureText: turnFailureText },
                    { submitText, stopTurn, retryTurn },
                    sendChat,
                )}
                onOpenThread={(thread) => openConversation(thread.id)}
                // "See all conversations" opens the left nav panel to the
                // conversations category (the same intent the rail's own
                // affordance fires).
                onOpenJournal={() => dispatch({ type: "open-home" })}
                // G5: the summary toggle lives in Settings > Assistant.
                onTurnOnSummaries={() =>
                    dispatch({ type: "open-settings", section: "assistant" })
                }
                onSeeAllThreads={() => {
                    selectCategory("conversations");
                    dispatch({ type: "expand-rail" });
                }}
                // The hero is now the SAME PowerComposer the chat renders (at
                // `hero` size) — so it takes the EXACT wiring the chat composer
                // already gets: the shared send-state, the `@`-mention pool +
                // controlled mention list, and the `/`-slash quick actions. No
                // parallel path; the old bespoke "+" menu's reference action is
                // now the native `@`-mention. (Gap, stated: the "+" menu's
                // action-shaped items — attach-a-source / new-material — are NOT
                // migrated as slash commands, because PowerComposer's slash
                // contract inserts TEXT only. Both stay reachable via the omnibar
                // / rail, and dropping a PDF onto the composer still grounds via
                // IngestHost's window drop listeners.)
                mentions={composerChips}
                onMentionsChange={(next) => {
                    // Reconcile against THIS conversation's pending grounding —
                    // added mentions ground it locally, removed ones unground it,
                    // all pre-send (no durable write until the first send).
                    for (const item of addedMentions(composerChips, next)) {
                        const ref = refFor(item.id);
                        if (ref !== undefined)
                            sendChat({
                                type: "pinned_grounding_source",
                                conversationId,
                                source: ref,
                            });
                    }
                    for (const item of removedMentions(pendingItems, next))
                        sendChat({
                            type: "unpinned_grounding_source",
                            conversationId,
                            sourceId: item.id,
                        });
                    // A removed reference chip leaves the references too.
                    const kept = composerMentions.filter((m) =>
                        next.some((n) => n.id === m.id),
                    );
                    if (kept.length !== composerMentions.length)
                        sendChat({ type: "edited_mentions", mentions: kept });
                }}
                composerCandidates={composerCandidates}
                composerCommands={chatSlashCommands}
                onAddSource={openFilePicker}
                pipelineProgress={taskProgress}
                arcProminence={arcProminence}
                onStartTour={requestGuidedTour}
                tip={
                    tipsDismissed
                        ? null
                        : (capabilityTips[tipIndex % capabilityTips.length] ??
                          null)
                }
                onDismissTips={() => setTipsDismissed(true)}
                phase={transition?.phase ?? "resting"}
                onExitTransitionEnd={transition?.onExited}
            />
        );
    };

    return { heroLayoutFor };
};

// HeroToChatTransition — bridges the hero's empty-conversation layout into
// the chat transcript on the FIRST message. This is the pragmatic
// approximation flagged in the task: a true shared-element composer (one
// PowerComposer instance that never unmounts, animated by a persistent shell
// slot) would need a portal-based composer host shared across
// WorkspaceLanding's centred grid and ChatFull's pinned-bottom layout — a
// bigger, riskier refactor of two independently-shaped layouts. Instead, the
// EXISTING hard-swap (`conversationCentre` in Centre.tsx) is held open for
// ONE extra beat: the moment a conversation flips empty → non-empty, this
// keeps rendering the (already-swapped-content, still on-screen) hero in an
// "exiting" phase — composer glides down + fades, caption/CTA fade
// (WorkspaceLanding.css) — and only swaps to `chat()` once that CSS
// transition reports done.
//
// `isExiting` is set SYNCHRONOUSLY, during render, the moment `isEmpty` is
// first observed to have flipped true → false — React's documented "adjust
// state during render" pattern (never inside a `useEffect`). This is
// load-bearing, not stylistic: an Effect fires one render AFTER the flip, so
// the render in between would return `chat()` for a single commit, unmounting
// the hero; when the Effect then flips `isExiting`, the hero would MOUNT
// FRESH already in its "exiting" end state (opacity 0, translated) with no
// prior painted frame to transition FROM — CSS transitions do not play on a
// value that was never observably different, so the exit would render as an
// instant blank hold instead of a glide (caught via instrumented DOM
// sampling while building this: the composer sat at opacity 0 for ~300ms
// with no animation, until the fallback below finally cleared it). Deriving
// synchronously keeps the SAME hero instance mounted across the flip, so the
// className change is a genuine, observable transition.
export const HeroToChatTransition = ({
    isEmpty,
    hero,
    chat,
}: {
    readonly isEmpty: boolean;
    readonly hero: (transition?: HeroTransitionProps) => ReactNode;
    readonly chat: () => ReactNode;
}): ReactNode => {
    const [isExiting, setIsExiting] = useState(false);
    const [prevIsEmpty, setPrevIsEmpty] = useState(isEmpty);

    if (isEmpty !== prevIsEmpty) {
        setPrevIsEmpty(isEmpty);
        if (prevIsEmpty && !isEmpty) setIsExiting(true);
    }

    // Safety net only — normally `onExited` (a real transitionend) clears
    // this first; 320ms comfortably outlasts the 200ms composer glide.
    useEffect(() => {
        if (!isExiting) return;
        const fallback = setTimeout(() => setIsExiting(false), 320);
        return () => clearTimeout(fallback);
    }, [isExiting]);

    if (isEmpty) return hero();
    if (isExiting)
        return hero({ phase: "exiting", onExited: () => setIsExiting(false) });
    return chat();
};
